import { reactive, ref, computed } from "vue";
import { WEB_ORIGIN } from "@/config/apiBase";
import { useRouter, useRoute } from "vue-router";
import axios from "axios";
import countries from "@/components/pages/countries";
import { Toaster, toast } from "vue-sonner";
import { useSubscription } from "@/composables/useSubscription";
import { useAdmin } from "@/composables/useAdmin";
import { canonicalSlug } from "@/utils/slug";

const IMAGE_LIMITS = { free: 5, premium: 15, business: 30 };
// Admins editing any post are not bound by a plan tier — effectively unlimited.
const ADMIN_IMAGE_LIMIT = 99;

export function UsePostEdit(view = "Post", serviceIdProp = null) {
  const router = useRouter();
  const route = useRoute();
  const serviceId = ref(serviceIdProp || route.query.servicesID || null);
  const { currentPlan } = useSubscription();
  const { isAdmin, fetchAdminStatus } = useAdmin();
  // Resolve admin role once so admins get the unlimited image allowance.
  fetchAdminStatus();
  const imageLimit = computed(() =>
    isAdmin.value ? ADMIN_IMAGE_LIMIT : IMAGE_LIMITS[currentPlan.value] || 5
  );

  // Loading state for submit
  const loading = ref(false);

  // Post redirect URL — set after successful creation so caller can control redirect timing
  const postRedirectUrl = ref("");
  // When the new ad expires (null = never) — shown on the post-success modal
  const postExpiresAt = ref(null);

  // Debounce utility
  function debounce(fn, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), delay);
    };
  }

  const form = reactive({
    serviceId: null,
    title: "",
    description: "",
    serviceType1: "",
    serviceType2: "",
    location: "",
    searchQuery: "",
    roomPrice: "",
    serviceType: "",
    email: "",
    photo: [],
    preview: null,
    phoneNumber: "",
    countryCode: "+1",
  });

  const previewImages = ref([]);
  const existingImages = ref([]);
  const newImages = ref([]);
  const removedImageIds = ref([]);
  const searchQuery = ref("");
  const suggestions = ref([]);
  const selectedPlace = ref(null);
  const hoverIndex = ref(null);
  const savedLat = ref(null);
  const savedLng = ref(null);
  const savedAddress = ref(null);

  const fetchSuggestions = async () => {
    if (searchQuery.value.length < 2) {
      suggestions.value = [];
      return;
    }
    try {
      const res = await axios.post("/api/places/autocomplete", {
        input: searchQuery.value,
      });
      suggestions.value = res.data || [];
    } catch (err) {
      console.error(err);
    }
  };

  // Debounced version
  const debouncedFetchSuggestions = debounce(fetchSuggestions, 500);

  const onInputChange = () => {
    if (
      selectedPlace.value &&
      searchQuery.value !== selectedPlace.value.description
    ) {
      selectedPlace.value = null;
    }
    debouncedFetchSuggestions();
  };

  const selectSuggestion = (place) => {
    selectedPlace.value = place;
    searchQuery.value = place.description || "";
    // form.location = searchQuery.value;
    form.location = place.description || "";
    suggestions.value = [];
  };

  const isSelectedPlace = (place) => {
    if (!selectedPlace.value) return false;
    if (selectedPlace.value.place_id && place.place_id)
      return selectedPlace.value.place_id === place.place_id;
    return selectedPlace.value.description === place.description;
  };

  const removeImage = (index) => {
    const previewUrl = previewImages.value[index];

    const existing = existingImages.value.find((img) => img.url === previewUrl);

    if (existing) {
      removedImageIds.value.push(existing.id); // mark delete
      existingImages.value = existingImages.value.filter(
        (img) => img.url !== previewUrl
      );
    } else {
      const newIndex = newImages.value.findIndex(
        (img) => img.url === previewUrl
      );
      if (newIndex !== -1) newImages.value.splice(newIndex, 1);
    }

    previewImages.value.splice(index, 1);
    form.photo.splice(index, 1);

    form.preview = previewImages.value[0] || null;
  };

  const storeAndPreviewFiles = (files) => {
    files.forEach((file) => {
      if (!file.type.startsWith("image/")) return;
      form.photo.push(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        previewImages.value.push(e.target.result);
        if (!form.preview) form.preview = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };
  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    const limit = imageLimit.value;
    const currentTotal = previewImages.value.length;

    if (currentTotal >= limit) {
      toast.error(
        isAdmin.value
          ? `Image limit reached (${limit} per post).`
          : `Image limit reached. Your ${currentPlan.value} plan allows ${limit} images per post.`
      );
      return;
    }

    const allowed = files.slice(0, limit - currentTotal);
    if (allowed.length < files.length) {
      toast.warning(`Only ${allowed.length} of ${files.length} images added. Limit: ${limit} per post.`);
    }

    allowed.forEach((file) => {
      const objectUrl = URL.createObjectURL(file);
      previewImages.value.push(objectUrl);
      form.photo.push(file);
      newImages.value.push({ file, url: objectUrl });
    });
    form.preview = previewImages.value[0];
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const limit = imageLimit.value;
    const currentTotal = previewImages.value.length;

    if (currentTotal >= limit) {
      toast.error(
        isAdmin.value
          ? `Image limit reached (${limit} per post).`
          : `Image limit reached. Your ${currentPlan.value} plan allows ${limit} images per post.`
      );
      event.target.value = "";
      return;
    }

    const allowed = files.slice(0, limit - currentTotal);
    if (allowed.length < files.length) {
      toast.warning(`Only ${allowed.length} of ${files.length} images added. Limit: ${limit} per post.`);
    }

    allowed.forEach((file) => {
      const objectUrl = URL.createObjectURL(file);
      previewImages.value.push(objectUrl);
      form.photo.push(file);
      newImages.value.push({ file, url: objectUrl });
    });
    form.preview = previewImages.value[0];
    event.target.value = "";
  };

  const loadAdData = async () => {
    if (!serviceId.value) return;

    try {
      const res = await axios.post("/api/servicescustom", {
        servicesID: serviceId.value,
      });
      const data = res.data.items[0];
      if (import.meta.env.DEV) {
        console.log(data);
      }
      form.serviceId = serviceId.value;
      form.title = data.servicesName;
      form.description = data.Description;
      form.serviceType1 = data.serviceType1;
      form.serviceType2 = data.serviceType2;
      form.serviceType = data.serviceTypeInfo;
      form.roomPrice = data.roomPrice;
      // Phone is stored as "<dial code> <number>" (e.g. "+1 5163503680") but the
      // form keeps them separate (countryCode picker + digits-only input). Split
      // it back apart; loop so legacy rows with doubled codes ("+1 +1 516…",
      // written by an old edit-mode save) collapse to a single code.
      {
        let phone = String(data.Phone || "").trim();
        let m;
        while ((m = phone.match(/^(\+\d{1,4})[\s-]+(.*)$/))) {
          form.countryCode = m[1];
          phone = m[2].trim();
        }
        let digits = phone.replace(/[^0-9]/g, "");
        // Crawled/legacy rows can store the US code fused on with no separator
        // ("17188556714", "+13473095344"). Peel it off so the input holds the
        // 10 digits the validator expects — only when the code really is +1,
        // so a genuine 11-digit foreign number is left for the user to fix.
        if (
          digits.length === 11 &&
          digits.startsWith("1") &&
          (form.countryCode || "+1") === "+1"
        ) {
          digits = digits.slice(1);
        }
        form.phoneNumber = digits;
      }
      form.email = data.Email;
      form.location = data.location;
      searchQuery.value = data.location;

      previewImages.value = [];
      existingImages.value = [];
      newImages.value = [];
      removedImageIds.value = [];
      form.photo = [] ;

      for (const img of data.serviceImages) {
        if (img.isActive === "true") {
          try {
            const fileRes = await axios.post(
              "/api/file/get",
              { fileName: img.filepath, albumName: 'default' }, // <-- add albumName if needed
              { responseType: "blob" }
            );
            // Validate blob has data
            if (!fileRes.data || fileRes.data.size === 0) {
              if (import.meta.env.DEV) {
                console.warn("Empty blob for image:", img.filepath);
              }
              continue;
            }
            // Check content-type
            const contentType = fileRes.data.type || fileRes.headers['content-type'] || "image/jpeg";
            if (!contentType.startsWith('image/')) {
              if (import.meta.env.DEV) {
                console.warn("Invalid content-type for image:", contentType);
              }
              continue;
            }
            const blob = new Blob([fileRes.data], { type: contentType });
            if (blob.size === 0) {
              if (import.meta.env.DEV) {
                console.warn("Created blob has zero size for image:", img.filepath);
              }
              continue;
            }
            const url = URL.createObjectURL(blob);
            previewImages.value.push(url);
            existingImages.value.push({
              id: img.imageId,
              filepath: img.filepath,
              url,
            });
            form.photo.push(blob);
            form.preview = previewImages.value[0] || null; // ⭐ first existing image
          } catch (error) {
            console.error("Error loading existing image:", error.message || error);
            continue;
          }
        }
      }
    } catch (e) {
      console.error("Error loading ad data:", e.message || e);
    }
  };



const resolveLocation = async (searchQueryValue) => {

  let lat = 0;
  let lng = 0;
  let currentLocation = searchQueryValue || "";
  let streetName = null, streetNumber = null, city = null, state = null, country = null, postalCode = null;

  const stored = localStorage.getItem("userLocation");
  let savedLat, savedLng, savedAddress, savedCity, savedCountry, savedState, savedStreetName, savedStreetNumber, savedPostalCode;

  if (stored) {
    try {
      const loc = JSON.parse(stored);
      savedLat = loc.lat;
      savedLng = loc.lng;
      savedAddress = loc.fullAddress;
      savedCity = loc.city || null;
      savedCountry = loc.country || null;
      savedState = loc.state || null;
      savedStreetName = loc.streetName || null;
      savedStreetNumber = loc.streetNumber || null;
      savedPostalCode = loc.postalCode || null;
      console.log("[resolveLocation] Loaded from localStorage:", loc);
    } catch (e) {
      console.error("[resolveLocation] Failed to parse userLocation from localStorage", e);
    }
  } else {
    console.log("[resolveLocation] No userLocation in localStorage");
  }

  // CASE 0: Location cleared
  if (!searchQueryValue || searchQueryValue.trim() === "") {
    console.log("[resolveLocation] Location input is empty or cleared. Returning lat/lng 0.");
    return {
      currentLocation: "",
      lat: 0,
      lng: 0,
      geo: {
        lat: 0,
        lng: 0,
        coordinates: [0, 0],
      },
      streetName: null,
      streetNumber: null,
      city: null,
      state: null,
      country: null,
      postalCode: null,
    };
  }

  // CASE 1: Location unchanged (from localStorage)
  if (searchQueryValue && savedAddress && searchQueryValue === savedAddress) {
    console.log("[resolveLocation] Location unchanged. Using saved lat/lng:", savedLat, savedLng, savedAddress);
    return {
      currentLocation: savedAddress,
      lat: savedLat ?? 0,
      lng: savedLng ?? 0,
      geo: {
        lat: savedLat ?? 0,
        lng: savedLng ?? 0,
        coordinates: [
          savedLng ?? 0,
          savedLat ?? 0,
        ],
      },
      streetName: savedStreetName,
      streetNumber: savedStreetNumber,
      city: savedCity,
      state: savedState,
      country: savedCountry,
      postalCode: savedPostalCode,
    };
  }

  // CASE 2: Location changed
  if (searchQueryValue) {
    console.log("[resolveLocation] Location changed. Resolving via API for:", searchQueryValue);
    try {
      const { data: auto } = await axios.post("/api/places/autocomplete", {
        input: searchQueryValue,
      });
      console.log("[resolveLocation] Autocomplete response:", auto);

      const placeID = auto?.[0]?.place_id;

      if (placeID) {
        const { data: place } = await axios.post("/api/placedetails", {
          placeId: placeID,
        });
        console.log("[resolveLocation] Placedetails response:", place);

        // /api/placedetails returns a flat { lat, lng } (both the Google and
        // Nominatim branches in routes/location.js are normalized to this
        // shape). The legacy Google-API code returned a nested
        // place.result.geometry.location.* — keep that as a fallback so
        // either response shape works.
        const flatLat = typeof place.lat === "number" ? place.lat : parseFloat(place.lat);
        const flatLng = typeof place.lng === "number" ? place.lng : parseFloat(place.lng);
        const nestedLoc = place?.result?.geometry?.location;
        const nestedLat = nestedLoc?.lat;
        const nestedLng = nestedLoc?.lng;
        lat = Number.isFinite(nestedLat) ? nestedLat
              : Number.isFinite(flatLat) ? flatLat
              : 0;
        lng = Number.isFinite(nestedLng) ? nestedLng
              : Number.isFinite(flatLng) ? flatLng
              : 0;

        // Address components: /api/placedetails now returns flat fields
        // (city/state/country/postalCode/streetName/streetNumber) for BOTH
        // Google and Nominatim modes. Read those directly.
        streetNumber = place.streetNumber ?? streetNumber;
        streetName = place.streetName ?? streetName;
        city = place.city ?? city;
        state = place.state ?? state;
        country = place.country ?? country;
        postalCode = place.postalCode ?? postalCode;

        // Fallback: legacy Google nested address_components shape, in case an
        // older backend is still deployed.
        if ((!city || !state) && place.result && place.result.address_components) {
          for (const comp of place.result.address_components) {
            if (comp.types.includes("street_number")) streetNumber = comp.long_name;
            if (comp.types.includes("route")) streetName = comp.long_name;
            if (comp.types.includes("locality")) city = comp.long_name;
            if (comp.types.includes("administrative_area_level_1")) state = comp.long_name;
            if (comp.types.includes("country")) country = comp.long_name;
            if (comp.types.includes("postal_code")) postalCode = comp.long_name;
          }
        }

        if (lat === 0 && lng === 0) {
          console.warn("[resolveLocation] API returned lat/lng as 0. Possible geocoding failure.");
        }

        return {
          currentLocation,
          lat,
          lng,
          geo: {
            lat: lat ?? 0,
            lng: lng ?? 0,
            coordinates: [
              lng ?? 0,
              lat ?? 0,
            ],
          },
          streetName,
          streetNumber,
          city,
          state,
          country,
          postalCode,
        };
      } else {
        console.warn("[resolveLocation] No valid place_id found in autocomplete response.", auto);
      }
    } catch (err) {
      console.error("[resolveLocation] Error in location resolution:", err);
    }
  }

  // FALLBACK
  console.warn("[resolveLocation] Location resolution failed, using fallback values.");
  return {
    currentLocation,
    lat: 0,
    lng: 0,
    geo: {
      lat: 0,
      lng: 0,
      coordinates: [0, 0],
    },
    streetName: null,
    streetNumber: null,
    city: null,
    state: null,
    country: null,
    postalCode: null,
  };
};


const submitForm = async () => {
  // Guard against double-submit (double-click / retry): if a submit is already
  // in flight, ignore further clicks. Prevents duplicate listings being created.
  if (loading.value) return;
  loading.value = true;
  try {
    // EDIT MODE
    if (view === "Edit") {
      const serviceId = form.serviceId;
      const {
        currentLocation,
        lat,
        lng,
        geo,
        streetName,
        streetNumber,
        city,
        state,
        country,
        postalCode,
      } = await resolveLocation(searchQuery.value);
      const payload = {
        servicesID: serviceId,
        servicesName: form.title,
        Description: form.description,
        location: currentLocation,
        Email: (form.email || "").trim(),
        serviceType1: form.serviceType1,
        serviceType2: form.serviceType2,
        serviceTypeInfo: form.serviceType,
        Phone: form.phoneNumber
          ? `${form.countryCode || ""} ${form.phoneNumber}`.trim()
          : null,
        roomPrice: form.roomPrice,
        isActive: "true",
        lat,
        lng,
        ...geo,
        streetName,
        streetNumber,
        city,
        state,
        country,
        postalCode,
      };
      console.log("PUT payload:", payload);
      await axios.put("/api/services", payload);
      for (const id of removedImageIds.value) {
        await axios.put("/api/updateserviceimage", {
          imageId: id,
          isActive: "false",
        });
      }
      for (const item of newImages.value) {
        const formData = new FormData();
        formData.append("images", item.file);
        const uploaded = await axios.post(
          "/api/file/upload",
          formData
        );
        const uploadedFile = uploaded.data.files && uploaded.data.files[0]?.filename;
        if (uploadedFile) {
          await axios.post("/api/serviceimage", {
            servicesID: form.serviceId,
            filepath: uploadedFile,
            isActive: "true",
          });
        }
      }
      toast.success("Ad Updated successfully!");
      window.location.reload();
      return;
    }
    // POST MODE
    const userID = localStorage.getItem("userid");
    const f = form;
    const {
      currentLocation,
      lat,
      lng,
      geo,
      streetName,
      streetNumber,
      city,
      state,
      country,
      postalCode,
    } = await resolveLocation(searchQuery.value);
    const postPayload = {
      userID,
      servicesName: f.title,
      Description: f.description,
      location: currentLocation,
      Email: (f.email || "").trim(),
      Phone: `${f.countryCode || ""} ${f.phoneNumber || ""}`.trim(),
      serviceType1: f.serviceType1,
      serviceType2: f.serviceType2,
      serviceTypeInfo: f.serviceType,
      roomPrice: f.roomPrice,
      isActive: "true",
      lat,
      lng,
      ...geo,
      streetName,
      streetNumber,
      city,
      state,
      country,
      postalCode,
    };
    console.log("POST payload:", postPayload);
    const { data: res } = await axios.post("/api/services", postPayload);
    console.log('[UsePostEditPages] /api/services response:', res);
    const serviceID = res.item.servicesID;
    const serviceName = res.item.servicesName;
    postExpiresAt.value = res.item.expiresAt || null;
    for (const file of f.photo) {
      const fd = new FormData();
      fd.append("images", file);
      const { data: up } = await axios.post(
        "/api/file/upload",
        fd
      );
      const uploadedFile = up.files && up.files[0]?.filename;
      if (uploadedFile) {
        await axios.post("/api/serviceimage", {
          servicesID: serviceID,
          userID,
          filepath: uploadedFile,
          isActive: "true",
        });
      }
    }
    // This is the slug of record — everything that later links to the post has
    // to reproduce it exactly, so the rule lives in one shared place now.
    const slug = canonicalSlug(serviceName);
    // Store only the slug in serviceslink
    const linkRes = await axios.post("/api/serviceslink", {
      url: slug,
      serviceID: serviceID,
      userID: userID,
    });
    console.log('[UsePostEditPages] /api/serviceslink response:', linkRes.data);
    const linkPass = `${WEB_ORIGIN}/view-post/${linkRes.data.item.url}`;
    console.log('[UsePostEditPages] Redirecting to:', linkPass);
    toast.success("Ad posted successfully!");
    localStorage.setItem(
      "viewPostContext",
      JSON.stringify({
        servicesID: serviceID,
        userID: userID,
        url: slug 
      })
    );
    // Store redirect URL — let caller (PostEditPage) decide when to navigate
    postRedirectUrl.value = linkPass;
    // Update ourlink in services table
    await axios.put("/api/services", {
      servicesID: serviceID,
      ourlink: linkPass,
    });
  } catch (err) {
    console.error(err);
    // Re-throw plan limit errors so PostEditPage can show the upgrade modal
    if (err?.response?.status === 403 && (
      err?.response?.data?.error === 'POST_LIMIT_REACHED' ||
      err?.response?.data?.error === 'IMAGE_LIMIT_REACHED'
    )) {
      throw err;
    }
    // Surface backend validation messages (e.g. invalid phone number) directly.
    if (err?.response?.status === 400 && err?.response?.data?.message) {
      toast.error(err.response.data.message);
      return;
    }
    toast.error("Something went wrong. Please try again!");
  } finally {
    loading.value = false;
  }
};


  if (view === "Edit" && serviceId.value) {
    loadAdData(serviceId.value); // fetch data for editing
  }

  return {
    form,
    previewImages,
    searchQuery,
    suggestions,
    selectedPlace,
    hoverIndex,
    fetchSuggestions,
    onInputChange,
    selectSuggestion,
    isSelectedPlace,
    removeImage,
    handleDrop,
    handleFileSelect,
    countries,
    submitForm,
    postRedirectUrl,
    postExpiresAt,
    loadAdData,
    existingImages,
    newImages,
    removedImageIds,
    loading,
    imageLimit,
    isAdmin,
  };
}
